export class VerticalSliderHTMLElement extends HTMLElement {
  private currentSlide: number;
  private editMode: boolean = false;
  private isAnimating: boolean = false;
  private scrollTrigger: any;
  private gsap: any;
  private timeline: any;
  private touchStart: any;

  constructor() {
    super();

    const currSlideAttr = this.getAttribute('data-current-slide');
    this.currentSlide = currSlideAttr ? Number.parseInt(currSlideAttr) : 0;

    this.scrollHandler = this.scrollHandler.bind(this);
    this.touchStartHandler = this.touchStartHandler.bind(this);
  }

  static get observedAttributes(): string[] {
    return ['data-current-slide', 'data-edit-mode'];
  }

  public attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if ((name !== 'data-current-slide' && name !== 'data-edit-mode') || oldValue === newValue) {
      return;
    }

    switch (name) {
      case 'data-current-slide':
        this.updateSlideIndex(Number.parseInt(newValue));
        break;
      case 'data-edit-mode':
        this.editMode = newValue === 'true';
        newValue === 'true' ? this.disableListeners() : this.enableListeners();
        break;
      default:
        break;
    }
  }

  private disableListeners(): void {
    window.removeEventListener('wheel', this.scrollHandler);
    window.removeEventListener('touchstart', this.touchStartHandler);
    window.removeEventListener('touchend', this.scrollHandler);
    this.scrollTrigger?.disable();
    this.currentSlide = 0;
  }

  private enableListeners(): void {
    window.addEventListener('wheel', this.scrollHandler, { passive: false });
    window.addEventListener('touchstart', this.touchStartHandler, { passive: false });
    window.addEventListener('touchend', this.scrollHandler, { passive: false });
    this.scrollTrigger?.enable();
  }

  private scrollHandler(e): void {
    if (this.isAnimating) {
      e.preventDefault();
      return;
    }

    if (!this.scrollTrigger?.isActive) {
      return;
    }

    const slides = Array.from(this.querySelectorAll<HTMLElement>('.slide'));
    const direction = e.type === 'wheel'
      ? e.deltaY < 0 ? 'up' : 'down'
      : this.touchStart - e.changedTouches[0].clientY < 0 ? 'up' : 'down';
    const newCurrentSlide = this.currentSlide + (direction === 'up' ? -1 : 1);

    if (newCurrentSlide < 0 && direction === 'up'
      || newCurrentSlide >= slides.length && direction === 'down'
    ) {
      if (e.type === 'touchend') {
        document.documentElement.style.touchAction = '';
      }
      return;
    }

    e.preventDefault();
    if (e.type === 'touchend') {
      document.documentElement.style.touchAction = 'none';
    }
    this.setAttribute('data-current-slide', newCurrentSlide.toString());
    this.slideTransition(
      slides[newCurrentSlide + (direction === 'up' ? 1 : -1)],
      slides[newCurrentSlide],
      direction
    );
  }

  private slideTransition(prevSlide: HTMLElement, currentSlide: HTMLElement, direction: string): void {
    this.timeline.to(
      prevSlide.querySelector('.slide__content'),
      {
        autoAlpha: 0,
        y: direction === 'up' ? 100 : -100,
        duration: 0.5,
        onStart: () => {
          this.isAnimating = true;
        }
      }
    ).to(
      direction === 'up' ? prevSlide : currentSlide,
      {
        autoAlpha: direction === 'up' ? 0 : 1,
        duration: 0.75,
        onComplete: () => this.gsap.set(prevSlide.querySelector('.slide__content'), { clearProps: true }) // removes inline style
      },
      '-=0.2'
    ).to(
      currentSlide,
      {
        duration: 0.25,
        onComplete: () => {
          this.isAnimating = false;
          this.timeline.clear(); // empties the timeline
        }
      }
    );
  }

  private touchStartHandler(e: TouchEvent): void {
    this.touchStart = e.changedTouches[0].clientY;
  }

  private updateSlideIndex(index: number): void {
    setTimeout(() => {
      this.currentSlide = index;

      const indicators = Array.from(this.querySelectorAll('.slider__indicator'));
      for (const el of indicators) {
        el.classList.remove('active');
      }
      indicators[index]?.classList.add('active');
    });
  }

  public connectedCallback(): void {
    setTimeout(async () => {
      const startup = await import('./verticalSlider-startup');
      const gsap = startup.gsap;
      const ScrollTrigger = startup.ScrollTrigger;

      this.gsap = gsap;
      this.timeline = gsap.timeline();

      this.scrollTrigger = ScrollTrigger.create({
        trigger: this,
        start: 'top-=25% top',
        end: '35% top',
        // markers: true,
        onToggle: self => {
          document.documentElement.style.touchAction = self.isActive ? 'none' : '';
        }
      });

      this.editMode ? this.disableListeners() : this.enableListeners();

      setTimeout(() => {
        this.scrollTrigger.refresh();
      }, 1000);
    });
  }

  public disconnectedCallback(): void {
    this.disableListeners();
    delete this.gsap;
    this.scrollTrigger?.kill();
  }
}
